// -------------------------------------------------------------------------
// 共通
// -------------------------------------------------------------------------
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin"
import { SplitText } from "gsap/SplitText"
export { gsap, ScrollTrigger, SplitText }
gsap.registerPlugin(ScrollTrigger, ScrambleTextPlugin)

const bodyElement = document.querySelector("body")

let lastWidth = window.innerWidth; // Encapsulated variable
window.addEventListener("resize", () => {
    let currentWidth = window.innerWidth;
    if (currentWidth !== lastWidth) {
        ScrollTrigger.refresh();
        lastWidth = currentWidth;
    }
});

// NOTE: スクロールトリガーアニメーションを適用する関数
export function applyScrollTriggerAnimation(elementsSelector, toggleClassName) {
    gsap.utils.toArray(elementsSelector).forEach(function (item) {
        gsap.to(item, {
            scrollTrigger: {
                trigger: item,
                start: "top 90%",
                end: 'bottom',
                toggleActions: "play none none none",
                // toggleClass: toggleClassName,
                once: true,
            },
            onStart: () => item.classList.add(toggleClassName), // アニメーション開始時にクラスを追加
        })
    })
}

// NOTE: タイピングアニメーションを適用する関数
export function applyTypingAnimation(selector) {
    gsap.utils.toArray(selector).forEach(item => {
        const text = item.textContent;
        item.textContent = ''; // Initially clear the text

        gsap.to(item, {
            scrollTrigger: {
                trigger: item,
                start: "top 90%",
                once: true,
                onEnter: () => {
                    gsap.to(item, {
                        duration: 1,
                        scrambleText: {
                            text: text,
                            chars: "■▪▌▐▬",
                            revealDelay: 0.5,
                            speed: 0.3
                        },
                        ease: "none",
                        onUpdate: function() {
                            const chars = "■▪▌▐▬";
                            const regex = new RegExp(`[${chars}]`, 'g');
                            this.targets()[0].innerHTML = this.targets()[0].textContent.replace(regex, (match) => `<span class="scrambling-text">${match}</span>`);
                        },
                        onComplete: () => {
                            item.innerHTML = text;
                            item.classList.add('is-typing');
                        }
                    });
                }
            }
        });
    });
}

// NOTE: ランダムタイピングアニメーションを適用する関数
export function applyTypingRandomAnimation(selector) {
    gsap.utils.toArray(selector).forEach(item => {
        let targets = [];

        // 子要素がある場合（HTMLタグを含む場合）は個別に処理
        if (item.children.length > 0) {
            const nodes = Array.from(item.childNodes);
            item.innerHTML = ''; // 一旦クリアして再構築

            nodes.forEach(node => {
                if (node.nodeType === Node.TEXT_NODE) {
                    // 空白のみのノードは無視（整形用の改行など）
                    if (node.textContent.trim() !== '') {
                        const span = document.createElement('span');
                        span.textContent = node.textContent;
                        item.appendChild(span);
                        targets.push(span);
                    }
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    item.appendChild(node);
                    targets.push(node);
                }
            });
        } else {
            // テキストのみの場合は要素自体をターゲットにする
            targets.push(item);
        }

        // 各ターゲットの元のテキストを保存してクリア
        const animTargets = targets.map(t => {
            const text = t.textContent;
            t.textContent = '';
            return { element: t, text: text };
        });

        // 親要素をトリガーにして一斉に開始
        ScrollTrigger.create({
            trigger: item,
            start: "top 90%",
            once: true,
            onEnter: () => {
                animTargets.forEach(target => {
                    gsap.to(target.element, {
                        duration: 1,
                        scrambleText: {
                            text: target.text,
                            chars: "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
                            speed: 0.15,
                        },
                        ease: "none",
                        onComplete: () => {
                            target.element.textContent = target.text;
                        }
                    });
                });
            }
        });
    });
}


// NOTE: フェードインアップ（テキスト版）
export function applyFadeInUpTextAnimation(selector) {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
        const mySplit = new SplitText(element, {
            type: "lines",
            linesClass: "u-animation-text-fadeinup__line"
        });

        const spans = [];
        mySplit.lines.forEach(line => {
            const span = document.createElement('span');
            // lineが空文字の場合、高さを維持するために不分離スペースを挿入
            span.innerHTML = line.innerHTML.trim() === '' ? '&nbsp;' : line.innerHTML;
            line.innerHTML = '';
            line.appendChild(span);
            spans.push(span);
        });

        spans.forEach((span, index) => {
            span.style.transitionDelay = `${index * 0.1}s`;
        });
        // is-enterクラスを付与する責務はapplyScrollTriggerAnimationに任せる
        applyScrollTriggerAnimation(element, "is-enter");
    });
}

// NOTE: 読み込み後のアニメーション開始
export function startAnimation() {
    // アニメーション開始: キービジュアルなど
    bodyElement.classList.add("is-animation-ready")
    // bodyElement.classList.remove("is-animation-fade")
    bodyElement.classList.remove("is-animation-loading")

    // スクロールでアニメーションさせるコンポーネント
    // 例: applyScrollTriggerAnimation(".xxx", "xxx--active"); → スクロールで.xxx--activeクラスがつく
    applyScrollTriggerAnimation(".u-animation", "is-enter") // 共通: is-enterをつけるだけのクラス
    applyScrollTriggerAnimation(".u-animation-fadeinup", "is-enter")
    applyScrollTriggerAnimation(".c-title", "is-enter")
    applyTypingAnimation(".c-title--typing .c-title__text");
    applyTypingAnimation(".u-animation-typing-scramble");
    applyTypingRandomAnimation(".c-page-title__en");
    applyFadeInUpTextAnimation(".u-animation-text-fadeinup");


    // fadeInStagger - 横並びの複数要素
    function fadeInStagger(elements, trigger, stagger = 0.05) {
        if (!elements.length) return;

        gsap.fromTo(
            elements,
            { autoAlpha: 0, y: 20 },
            {
                autoAlpha: 1,
                y: 0,
                duration: 0.4,
                delay: 0.3, // split text
                stagger: stagger,
                scrollTrigger: {
                    trigger: trigger,
                    start: "top 70%",
                    end: "bottom",
                    toggleActions: "play none none none",
                    once: true,
                },
            }
        );
    }

    // アニメーション適用
    document.querySelectorAll(".u-animation-stagger").forEach((section) => {
        const elements = section.querySelectorAll(":scope > *");
        if (elements.length > 0) {
            fadeInStagger(elements, section);
        }
    });

    applyParallaxAnimation();
}

// NOTE: パララックスアニメーションを適用する関数
export function applyParallaxAnimation() {
    gsap.utils.toArray('.u-animation-parallax').forEach(el => {
        const parent = el.parentElement;
        gsap.fromTo(el, {
            y: '0%',
        }, {
            y: '20%',
            ease: 'none',
            scrollTrigger: {
                trigger: parent,
                start: 'top top',
                end: 'bottom top',
                scrub: true,
            },
        });
    });
}

// セクションごとのアニメーションを設定
document.addEventListener('DOMContentLoaded', function() {
    if (document.querySelector('.u-animation__bg')) {
        gsap.to('.u-animation__bg', {
            opacity: '1',
            scrollTrigger: {
                trigger: '.u-animation__bg',
                start: "top 100%",
                end: 'top 0',
                scrub: true,
                invalidateOnRefresh: true,
            },
        });
    }
});
